import type {
  CanvasEdge,
  CanvasMode,
  CanvasModeConfiguration,
  DomainCanvas,
  JsonValue,
} from "./domain/types";
import type { CanvasCommand, CanvasEditor } from "./controller/types";
import { createCanvasNode } from "./domain/canvas";

type CanvasLeafCommand = Exclude<CanvasCommand, { type: "batch" }>;

export const CANVAS_MODE_CONFIGURATIONS: Readonly<
  Record<CanvasMode, CanvasModeConfiguration>
> = {
  freeform: {
    mode: "freeform",
    label: "Freeform",
    description: "A general canvas without spatial constraints.",
    defaultNodeType: "sticky",
    layoutStrategy: "free",
    features: {
      references: true,
      frames: true,
      freehand: true,
      connections: true,
    },
    settings: {},
  },
  mindmap: {
    mode: "mindmap",
    label: "Mind map",
    description: "Organizes connected content in a tree layout.",
    defaultNodeType: "text",
    layoutStrategy: "tree",
    features: {
      references: true,
      frames: false,
      freehand: false,
      connections: true,
    },
    settings: { direction: "right" },
  },
  research: {
    mode: "research",
    label: "Research",
    description: "Emphasizes sources, annotations, and evidence links.",
    defaultNodeType: "reference",
    layoutStrategy: "free",
    features: {
      references: true,
      frames: true,
      freehand: true,
      connections: true,
    },
    settings: { referenceDensity: "comfortable" },
  },
  project: {
    mode: "project",
    label: "Project",
    description: "Uses frames to represent stages or swimlanes.",
    defaultNodeType: "sticky",
    layoutStrategy: "columns",
    features: {
      references: true,
      frames: true,
      freehand: false,
      connections: true,
    },
    settings: { columns: ["To do", "In progress", "Done"] },
  },
};

export function resolveCanvasModeConfiguration(
  mode: CanvasMode,
  override?: Partial<Omit<CanvasModeConfiguration, "mode">>,
): CanvasModeConfiguration {
  const base = CANVAS_MODE_CONFIGURATIONS[mode];
  return {
    ...base,
    ...override,
    mode,
    features: { ...base.features, ...override?.features },
    settings: {
      ...base.settings,
      ...(override?.settings as Record<string, JsonValue> | undefined),
    },
  };
}

export interface CanvasModeStrategy {
  readonly mode: CanvasMode;
  readonly actionLabel: string;
  commandsForCanvas(
    canvas: DomainCanvas,
    options?: CanvasModeBehaviorOptions,
  ): CanvasLeafCommand[];
  prepareEdge?(edge: CanvasEdge): CanvasEdge;
}

export interface CanvasModeBehaviorOptions {
  projectColumns?: readonly string[];
}

function mindmapCommands(canvas: DomainCanvas): CanvasLeafCommand[] {
  const outgoing = new Map<string, string[]>();
  const incoming = new Set<string>();
  for (const edge of canvas.edges) {
    const targets = outgoing.get(edge.sourceNodeId) ?? [];
    targets.push(edge.targetNodeId);
    outgoing.set(edge.sourceNodeId, targets);
    incoming.add(edge.targetNodeId);
  }
  const roots = canvas.nodes
    .filter((node) => !incoming.has(node.id))
    .map((node) => node.id);
  const queue = (
    roots.length > 0 ? roots : canvas.nodes.slice(0, 1).map((node) => node.id)
  ).map((id) => ({ id, depth: 0 }));
  const seen = new Set<string>();
  const rowsByDepth = new Map<number, number>();
  const commands: CanvasLeafCommand[] = [];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined || seen.has(current.id)) continue;
    seen.add(current.id);
    const row = rowsByDepth.get(current.depth) ?? 0;
    rowsByDepth.set(current.depth, row + 1);
    commands.push({
      type: "update-node",
      id: current.id,
      patch: {
        geometry: {
          x: current.depth * 300,
          y: row * 170,
        },
      },
    });
    for (const targetId of outgoing.get(current.id) ?? []) {
      queue.push({ id: targetId, depth: current.depth + 1 });
    }
  }
  for (const node of canvas.nodes) {
    if (seen.has(node.id)) continue;
    const row = rowsByDepth.get(0) ?? 0;
    rowsByDepth.set(0, row + 1);
    commands.push({
      type: "update-node",
      id: node.id,
      patch: { geometry: { x: 0, y: row * 170 } },
    });
  }
  return commands;
}

function projectCommands(
  canvas: DomainCanvas,
  options: CanvasModeBehaviorOptions = {},
): CanvasLeafCommand[] {
  const configured = canvas.settings.columns;
  const columns =
    Array.isArray(configured) &&
    configured.every((entry): entry is string => typeof entry === "string")
      ? configured
      : options.projectColumns ?? ["To do", "In progress", "Done"];
  const existing = new Set(
    canvas.nodes.flatMap((node) =>
      node.type === "frame" && typeof node.props.projectColumnKey === "string"
        ? [node.props.projectColumnKey]
        : [],
    ),
  );
  return columns.flatMap((title, index): CanvasLeafCommand[] => {
    const key = `column:${index}`;
    if (
      existing.has(key) ||
      [...existing].some((legacyKey) => legacyKey.startsWith(`${key}:`))
    ) {
      return [];
    }
    return [
      {
        type: "create-node",
        node: createCanvasNode({
          type: "frame",
          geometry: {
            x: index * 380,
            y: 0,
            width: 340,
            height: 720,
            zIndex: `0-project-frame-${index.toString().padStart(3, "0")}`,
          },
          props: { title, projectColumnKey: key },
        }),
      },
    ];
  });
}

function researchCommands(canvas: DomainCanvas): CanvasLeafCommand[] {
  return canvas.edges.flatMap((edge): CanvasLeafCommand[] =>
    edge.relationType === undefined
      ? [
          {
            type: "update-edge",
            id: edge.id,
            patch: {
              relationType: "supports",
              props: { arrowEnd: true, color: "#4f46e5" },
            },
          },
        ]
      : [],
  );
}

export const CANVAS_MODE_STRATEGIES: Readonly<
  Record<CanvasMode, CanvasModeStrategy>
> = {
  freeform: {
    mode: "freeform",
    actionLabel: "Arrange freeform canvas",
    commandsForCanvas: () => [],
  },
  mindmap: {
    mode: "mindmap",
    actionLabel: "Apply tree layout",
    commandsForCanvas: mindmapCommands,
    prepareEdge: (edge) => ({
      ...edge,
      relationType: edge.relationType ?? "child",
      props: { ...edge.props, arrowEnd: true },
    }),
  },
  project: {
    mode: "project",
    actionLabel: "Create project columns",
    commandsForCanvas: projectCommands,
  },
  research: {
    mode: "research",
    actionLabel: "Organize evidence links",
    commandsForCanvas: researchCommands,
    prepareEdge: (edge) => ({
      ...edge,
      relationType: edge.relationType ?? "supports",
      props: { ...edge.props, arrowEnd: true, color: "#4f46e5" },
    }),
  },
};

export function resolveCanvasModeStrategy(
  mode: CanvasMode,
): CanvasModeStrategy {
  return CANVAS_MODE_STRATEGIES[mode];
}

export function applyCanvasModeBehavior(
  editor: CanvasEditor,
  options?: CanvasModeBehaviorOptions,
): ReturnType<CanvasEditor["dispatch"]> {
  const snapshot = editor.getSnapshot();
  const commands = resolveCanvasModeStrategy(
    snapshot.canvas.mode,
  ).commandsForCanvas(snapshot.canvas, options);
  if (commands.length === 0) return null;
  return editor.dispatch({ type: "batch", commands });
}

export interface SwitchCanvasModeOptions {
  /**
   * Applies the target mode's layout/content behavior in the same undo step.
   * Defaults to false so changing the mode never moves or creates nodes unless
   * the host has explicitly confirmed that impact with the user.
   */
  applyBehavior?: boolean;
  behavior?: CanvasModeBehaviorOptions;
}

export function switchCanvasMode(
  editor: CanvasEditor,
  target: CanvasMode,
  options: SwitchCanvasModeOptions = {},
): ReturnType<CanvasEditor["dispatch"]> {
  const snapshot = editor.getSnapshot();
  const targetCanvas =
    snapshot.canvas.mode === target
      ? snapshot.canvas
      : { ...snapshot.canvas, mode: target };
  const commands: CanvasLeafCommand[] = [
    ...(snapshot.canvas.mode === target
      ? []
      : [{ type: "set-mode" as const, mode: target }]),
    ...(options.applyBehavior === true
      ? resolveCanvasModeStrategy(target).commandsForCanvas(
          targetCanvas,
          options.behavior,
        )
      : []),
  ];
  if (commands.length === 0) return null;
  return editor.dispatch({ type: "batch", commands });
}

export function prepareEdgeForCanvasMode(
  edge: CanvasEdge,
  mode: CanvasMode,
): CanvasEdge {
  return resolveCanvasModeStrategy(mode).prepareEdge?.(edge) ?? edge;
}
